// ServiceTariffPage.tsx 
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {CURRENCY_TYPES, IServiceTariffModel, LIST_TYPES, initialServiceTariff, validationServiceTariff} from './ServiceTariffModel'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createServiceTariff, deleteServiceTariff, loadServiceTariffs, updateServiceTariff} from './Api'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {ServiceTariffTable} from './ServiceTariffTable'

const ServiceTariffPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [serviceTariffs, setServiceTariffs] = useState<IServiceTariffModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)

  const tabOptions: TabOption<'form' | 'detail' | 'list'>[] = [
    {key: 'form', title: 'Sigorta Tarife Listesi', icon: 'bi bi-geo-alt'},
    
  ]

  const fetchServiceTariffs = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)

      try {
        const response = await loadServiceTariffs(page, 5, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch servicetariffs')
        }
        setServiceTariffs(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // İlk yükleme
  useEffect(() => {
    if (tab === 'list') {
      fetchServiceTariffs(0)
    }
  }, [tab, fetchServiceTariffs])

  const handleSave = async (values: IServiceTariffModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateServiceTariff({...values, id: selectedId})
        showSuccessToast('Sigorta Tarifesi başarıyla güncellendi')
      } else {
        await createServiceTariff(values)
        showSuccessToast('Sigorta Tarifesi başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchServiceTariffs(0)
      resetForm()
      setSelectedId(null)
      setTab('form')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback((serviceTariff: IServiceTariffModel, formik: FormikProps<IServiceTariffModel>) => {
    formik.setValues(serviceTariff)
    setSelectedId(serviceTariff.id)
    setTab('form')
  }, [])

  const handleDeleteClick = useCallback((serviceTariff: IServiceTariffModel) => {
    setSelectedId(serviceTariff.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteServiceTariff(selectedId)
        showSuccessToast('Sigorta Tarifesi başarıyla silindi')
        await fetchServiceTariffs(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Sigorta Tarifesi silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchServiceTariffs(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchServiceTariffs]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchServiceTariffs(currentPage, column, order)
    },
    [currentPage, fetchServiceTariffs]
  )

  return (
    <>
      <Formik
        initialValues={initialServiceTariff}
        validationSchema={validationServiceTariff}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          return (
            <>
              <FormTabHeader
                formId='serviceTariffForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'> 
                {/* Form  Tabları */}
                {(tab === 'form' ) && (
                  <Form id='serviceTariffForm' className='form card-body'>
                    {tab === 'form' && (
                      <>
                      <div className='row mb-6'>
  <div className='col-md-6'>
    <SelectInput
      name='currency'
      label='Para Birimi'
      options={CURRENCY_TYPES}
    />
  </div>

  <div className='col-md-6'>
    <TextInput
      name='IMMDescription'
      label='IMM (İhtiyari Mali Mesuliyet) Açıklama'
    />
  </div>
</div>

<div className='row mb-6'>
    <div className='col-md-6'>
        <TextInput
        name='babySeat'
        label='Bebek Koltuğu'
        type='number'
        />
    </div>
    <div className='col-md-6'>
         <TextInput
        name='IMMDescription'
        label='Muafiyet Sigorta Açıklama'
        />
    </div>
</div>

<div className='row mb-6'>
  <div className='col-md-6'>
    <TextInput name='navigation' type='number' label='Navigasyon' />
  </div>
  <div className='col-md-6'>
    <TextInput
      name='youngDriverDescription'
      label='Genç Sürücü Açıklama'
    />
  </div>
</div>

<div className='row mb-6'>
  {/* Ek Sürücü */}
  <div className='col-md-6'>
    <TextInput
      name='additionalDriver'
      label='Ek Sürücü'
      type='number'
    />
  </div>

  {/* Paket Sigorta Hizmeti 1: adet + name */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='insurancePackageServiceOne.count'
        label='Adet'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='insurancePackageServiceOne.name'
        label='Paket Sigorta Hizmeti 1'
        type='text'
      />
    </div>
  </div>
</div>


<div className='row mb-6'>
  {/* wifi bedeli: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='internetFee'
        label='Wifi Bedeli'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='max_internet_day'
        label='Max Gün'
        type='text'
      />
    </div>
  </div>

  <div className='col-md-6'>
    <TextInput
    name='insurancePackageServiceOne.description'
    label='Paket Sigorta Açıklama 1'
    />
  </div>
</div>


<div className='row mb-6'>
  {/* max çocuk koltuğu: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='childSeat.maxDay'
        label='Max.Çocuk Koltuğu (Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='childSeat.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

   {/* Paket Sigorta Hizmeti 2: adet + name */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='insurancePackageServiceTwo.count'
        label='Adet'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='insurancePackageServiceTwo.name'
        label='Paket Sigorta Hizmeti 2'
        type='text'
      />
    </div>
  </div>
</div>


<div className='row mb-6'>
  {/* Max Navigasyon: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='maxNavigation.maxDay'
        label='Max.Navigasyon(Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='maxNavigation.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

  <div className='col-md-6'>
    <TextInput
    name='insurancePackageServiceTwo.description'
    label='Paket Sigorta Açıklama 2'
    />
  </div>
</div>


<div className='row mb-6'>
  {/* Mini Hasar: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='miniDamage.maxDay'
        label='Max. Mini Hasar(Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='miniDamage.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

   {/* Paket Sigorta Hizmeti 3: name + adet */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='insurancePackageServiceThree.count'
        label='Adet'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='insurancePackageServiceThree.name'
        label='Paket Sigorta Hizmeti 3'
        type='text'
      />
    </div>
  </div>
</div>


<div className='row mb-6'>
  {/* Max SCDW: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='SCDW.maxDay'
        label='Max.SCDW(Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='SCDW.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

  <div className='col-md-6'>
    <TextInput
    name='insurancePackageServiceThree.description'
    label='Paket Sigorta Açıklama 3'
    />
  </div>
</div>


<div className='row mb-6'>
  {/* Paket Sigorta Hizmeti 1: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='LCF.maxDay'
        label='Max.LCF(Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='LCF.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

   {/* Paket Sigorta Hizmeti 4: name +  adet */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='insurancePackageServiceFour.count'
        label='Adet'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='insurancePackageServiceFour.name'
        label='Paket Sigorta Hizmeti 4'
        type='text'
      />
    </div>
  </div>
</div>


<div className='row mb-6'>
  {/* Max Ek Sürücü: adet + açıklama */}
  <div className='col-md-6 d-flex align-items-end'>
    <div style={{ flex: '0 0 80px', marginRight: '8px' }}>
      <TextInput
        name='maxAdditionalDriver.maxDay'
        label='Max.Ek Sürücü(Gün)'
        type='number'
      />
    </div>
    <div style={{ flex: 1 }}>
      <TextInput
        name='maxAdditionalDriver.description'
        label='Açıklama'
        type='text'
      />
    </div>
  </div>

  <div className='col-md-6'>
    <TextInput
    name='insurancePackageServiceFour.description'
    label='Paket Sigorta Açıklama 4'
    />
  </div>
</div>

<div className='row mb-6'>

<div className='col-md-6'>
    <TextInput
    name='PAIDescription'
    label='PAI Açıklaması'
    />
</div>

<div className='col-md-6'>
     <SelectInput
    name='listingMethod'
    label='Listeleme Yöntemi'
    options={LIST_TYPES}
    />
</div>

</div>
                      </>
                    )}
                   
                  </Form>
                )}
              </div>
            </>
          )
        }}
      </Formik>

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {ServiceTariffPage}
